import { Message } from "../models/Message";
import { UIMessage } from "../ui/components/UIMessage";
import { Fire } from "./FireClient";
import { My } from "./My";

import { FireClientMsg } from "./FireClientMsg";
export class MessageLoader {
  allMessages: Message[] = [];
  oldMessages: Message[] = [];
  uiMessages: UIMessage[] = [];
  nrNew = 0;
  hasLoaded = false;
  private isLoading = false;
  oldMessagesFromDate: Date | null = null;
  private unsubscribeFn: () => void = () => { };

  // roomId → "방을 열었을 때의 최신 메시지 시각"(ms). 실시간 구독은 이 시각 이후 메시지만 감시하므로
  // created 가 이 값보다 크면 = 방을 연 뒤 도착한 메시지. 서버 timestamp 기반이라 클럭 스큐에 안전.
  // 미확인 알림(isNew)은 지금 보고 있는 방에선 안 켜져 "방금 도착" 판단에 못 쓰기 때문에 별도로 노출.
  static roomOpenBoundaryMs = new Map<string, number>();

  static isFreshArrival(roomId: string, created: unknown): boolean {
    const boundary = MessageLoader.roomOpenBoundaryMs.get(roomId);
    if (boundary === undefined) return false; // 경계 미확정 → 보수적으로 과거(접기 대상) 취급
    const t = new Date(created as string | number | Date).getTime();
    return !Number.isNaN(t) && t > boundary;
  }

  constructor(private roomId: string, private onNewMessages: (msgs: UIMessage[], newParentMessages: boolean, visibleMsgs: Message[], invisibleMsg: Message[]) => void) {
  }

  unsubscribe() {
    this.unsubscribeFn();
  }

  async loadOlderMessageAndSubscribeToNew(startDate: Date | null = null, limitCount: number = FireClientMsg.NrMsgsPerLoad): Promise<number> {
    if (this.isLoading) return 0;
    this.isLoading = true;
    try {
      if (!this.hasLoaded) {
        // Initial batch from Firestore (parents only, newest first)
        const initialParents = this.dedupeMessages(await Fire.msg.getMessages(My.user.id, this.roomId, startDate, limitCount));

        // Merge with any pre-seeded messages (e.g., getMessagesAfter result pushed before calling this)
        // Preserve all preloaded parents so "after" messages aren't lost.
        const mergedAll = this.dedupeMessages((this.allMessages ?? []).concat(initialParents));

        this.allMessages = mergedAll.map(m => {
          m.inProgress = false; // snapshot listener will handle updates for new ones; initial ones are static baseline
          return m;
        });
        // Keep oldMessages as the baseline visible set used by subscription handler
        this.oldMessages = this.dedupeMessages((this.oldMessages ?? []).concat(initialParents));
        await this.loadSummaries(this.allMessages);

        if (this.allMessages.length > 0) {
          const newestMessage = await Fire.msg.getLastMessage(My.user.id, this.roomId);
          if (newestMessage) {
            this.oldMessagesFromDate = new Date(newestMessage.created);
            MessageLoader.roomOpenBoundaryMs.set(this.roomId, this.oldMessagesFromDate.getTime());
          }
          else {
            this.oldMessagesFromDate = null;
            MessageLoader.roomOpenBoundaryMs.set(this.roomId, 0); // 빈 방 → 이후 도착 전부 새 메시지
          }
        } else {
          this.oldMessagesFromDate = null;
          MessageLoader.roomOpenBoundaryMs.set(this.roomId, 0);
        }

        this.unsubscribeFn();
        this.unsubscribeFn = Fire.msg.subscribeToNewMessages(My.user.id, this.roomId, this.oldMessagesFromDate, async (newMsgs) => {
          let visibleMsgs: Message[] = [];
          let invisibleMsgs: Message[] = [];
          newMsgs.forEach(msg => {
            if (msg.parentMessageId) {
              let parent = this.allMessages.find(m => m.id == msg.parentMessageId);
              if (parent) {
                visibleMsgs.push(msg);
              } else {
                invisibleMsgs.push(msg);
              }
            } else {
              visibleMsgs.push(msg);
            }
            // Update summary for parents even if invisible
            if (msg.parentMessageId) {
              let parent = this.allMessages.find(m => m.id == msg.parentMessageId);
              if (parent) {
                // 🔧 리액션(emoticon)은 replyCount에 포함하지 않음
                if (!msg.emoticon) {
                  parent.replyCount = (parent.replyCount ?? 0) + 1;
                  parent.lastReply = msg;
                  if (msg.user.id !== My.user.id) {
                    parent.unreadReplyCount = (parent.unreadReplyCount ?? 0) + 1;
                  }
                }
              }
            }
          });
          // console.log("newMsgs2", visibleMsgs, invisibleMsgs);
          let prevMessageLength = this.uiMessages.length;
          if (visibleMsgs.length > 0) {
            const merged = visibleMsgs.concat(this.oldMessages);
            this.oldMessages = this.dedupeMessages(merged);
            // Merge into allMessages without losing any preloaded messages
            this.allMessages = this.dedupeMessages(this.allMessages.concat(visibleMsgs));
          }
          let _messages = this.getUIMessages();
          let newParentMessages = _messages.length > prevMessageLength;
          this.onNewMessages(_messages, newParentMessages, visibleMsgs, invisibleMsgs);
        });

        this.hasLoaded = true;
        return this.oldMessages.filter(m => !m.parentMessageId).length;
      } else if (this.allMessages.length > 0) {
        let lastMessage = this.allMessages[0];
        let moreMessages: Message[] = [];
        if (startDate) {
          moreMessages = await Fire.msg.getMessagesBetweenCreated(My.user.id, this.roomId, new Date(lastMessage.created), startDate, limitCount);
        } else {
          moreMessages = await Fire.msg.getMessages(My.user.id, this.roomId, new Date(lastMessage.created), limitCount);
        }
        await this.loadSummaries(moreMessages);
        this.allMessages = this.dedupeMessages(this.allMessages.concat(moreMessages));
        this.oldMessages = this.dedupeMessages(this.oldMessages.concat(moreMessages));
        const parentCount = moreMessages.filter(m => !m.parentMessageId).length;
        return parentCount;
      }
      else
        return 0;
    } finally {
      this.isLoading = false;
    }
  }

  getUIMessages(): UIMessage[] {
    // Oldest first, newest last in list.
    const dedupedMessages = this.dedupeMessages(this.allMessages);
    const allMsgs = dedupedMessages.sort((a, b) => a.created > b.created ? 1 : -1);
    // console.log("allMsgs", allMsgs);
    let _parents: UIMessage[] = [];
    let _parentList = allMsgs.filter(m => !m.parentMessageId);
    let childMap = new Map<string, Message[]>(); // parentId / message
    let lastDate: string | null = null;
    const addDateIfNewDay = (uimsg: UIMessage) => {
      const msgDate = new Date(uimsg.msg.created).toDateString();
      if (lastDate !== msgDate) {
        uimsg.isNewDay = true;
        lastDate = msgDate;
      }
    };

    allMsgs.filter(m => !!m.parentMessageId).forEach(m => {
      if (m.emoticon && m.replyToMessageId) {
        if (!childMap.has(m.replyToMessageId)) {
          childMap.set(m.replyToMessageId, [])
        }
        childMap.get(m.replyToMessageId)!.push(m);
      } else {
        if (!childMap.has(m.parentMessageId!)) {
          childMap.set(m.parentMessageId!, [])
        }
        childMap.get(m.parentMessageId!)!.push(m);
      }
    });

    _parentList.forEach(msg => {
      let kids: UIMessage[] = childMap.get(msg.id)?.map(c => {
        let grandKids = childMap.get(c.id)?.map(gc => new UIMessage(gc, [])) ?? [];
        let kid = new UIMessage(c, grandKids);
        return kid;
      }) ?? [];

      let parent = new UIMessage(msg, kids);
      // 🔧 리액션(emoticon)은 댓글 수/마지막 답글에서 제외
      const replyKids = kids.filter(k => !k.msg.emoticon);
      parent.replyCount = msg.replyCount ?? replyKids.length;
      parent.lastReply = msg.lastReply ?? (replyKids.length > 0 ? replyKids[replyKids.length - 1].msg : null);
      parent.repliesLoaded = msg.repliesLoaded || false; // Important: direct sync from raw message
      parent.unreadReplyCount = msg.unreadReplyCount || 0;
      _parents.push(parent);
    });
    _parents.forEach(uimsg => {
      uimsg.isNewDay = false;
      addDateIfNewDay(uimsg);
    });

    this.allMessages = allMsgs;
    this.uiMessages = _parents;
    return this.uiMessages;
  }

  private dedupeMessages(messages: Message[]): Message[] {
    const map = new Map<string, Message>();

    messages.forEach((msg) => {
      const existing = map.get(msg.id);
      if (!existing) {
        map.set(msg.id, msg);
        return;
      }

      const getCreatedTime = (c: any) => {
        if (!c) return 0;
        if (typeof c.toMillis === 'function') return c.toMillis();
        if (c instanceof Date) return c.getTime();
        return new Date(c).getTime();
      };

      const existingCreated = getCreatedTime(existing.created);
      const currentCreated = getCreatedTime(msg.created);

      if (currentCreated >= existingCreated) {
        // 🔧 FIX: 새 메시지를 선택할 때 기존 메시지의 reply 관련 속성 보존
        // Firebase에서 새로 가져온 메시지에는 이 속성들이 없을 수 있음
        const merged = { ...msg };
        if (existing.replyCount !== undefined && msg.replyCount === undefined) {
          merged.replyCount = existing.replyCount;
        }
        if (existing.lastReply !== undefined && msg.lastReply === undefined) {
          merged.lastReply = existing.lastReply;
        }
        if (existing.repliesLoaded !== undefined && msg.repliesLoaded === undefined) {
          merged.repliesLoaded = existing.repliesLoaded;
        }
        if (existing.unreadReplyCount !== undefined && msg.unreadReplyCount === undefined) {
          merged.unreadReplyCount = existing.unreadReplyCount;
        }
        // 🔧 trigger가 추가한 webhookDelivery를 항상 최신으로 반영
        if ((msg as any).webhookDelivery) {
          (merged as any).webhookDelivery = (msg as any).webhookDelivery;
        } else if ((existing as any).webhookDelivery) {
          (merged as any).webhookDelivery = (existing as any).webhookDelivery;
        }
        map.set(msg.id, merged);
      } else {
        // created가 기존보다 작아도 text/isEdited가 변경되었으면 업데이트 반영
        if (msg.isEdited && (!existing.isEdited || existing.text !== msg.text)) {
          existing.text = msg.text;
          existing.isEdited = msg.isEdited;
        }
        // 🔧 기존본을 유지하면서 webhookDelivery가 추가되었다면 병합
        if ((msg as any).webhookDelivery && !(existing as any).webhookDelivery) {
          (existing as any).webhookDelivery = (msg as any).webhookDelivery;
        }
      }
    });

    return Array.from(map.values());
  }


  static getNextMessage(current: UIMessage | null, parents: UIMessage[]): UIMessage | null {
    let next: UIMessage | null = null;
    if (current) {
      const hasChildren = current.children && current.children.length > 0;
      const isChild = Boolean(current.msg.parentMessageId);

      if (hasChildren && !isChild) {
        next = current.children[0];
        // Go to first child
      } else if (isChild) {
        // Go to next sibling
        let parent = parents.find(m => m.msg.id == current.msg.parentMessageId);
        if (parent) {
          let index = parent.children.findIndex(c => c.msg.id == current.msg.id);
          if (index + 1 < parent.children.length) {
            next = parent.children[index + 1];
          }
        }
      }

      if (next == null) {
        // Go to next parent
        let parentId = current.msg.parentMessageId ?? current.msg.id;
        let index = parents.findIndex(m => m.msg.id == parentId);
        if (index > -1 && index + 1 < parents.length) {
          next = parents[index + 1];
        }
      }
    }

    if (next?.msg.emoticon) {
      return this.getNextMessage(next, parents);
    } else {
      return next;
    }
  }

  async loadSummaries(parents: Message[]) {
    // Only load summaries for messages that haven't been loaded yet and have no children locally
    const needsSummary = parents.filter(p => !p.parentMessageId && !p.repliesLoaded);
    if (needsSummary.length === 0) return;

    const parentIds = needsSummary.map(p => p.id);

    // 🔧 배치 방식: 카운트(7쿼리 병렬) + 리액션(1쿼리 배치) = 8쿼리
    // 기존: getChildSummary × 7 = 21쿼리 (카운트+last5+리액션 각각)
    const [counts, reactions] = await Promise.all([
      Fire.msg.getReplyCountsBatch(My.user.id, parentIds),
      Fire.msg.getReactionsByParentIds(My.user.id, parentIds)
    ]);

    // 부모별 리액션 수 계산 (카운트에서 차감)
    const reactionCountByParent = new Map<string, number>();
    reactions.forEach(r => {
      const pid = r.parentMessageId!;
      reactionCountByParent.set(pid, (reactionCountByParent.get(pid) || 0) + 1);
    });

    needsSummary.forEach(p => {
      const totalChildren = counts.get(p.id) || 0;
      const reactionCount = reactionCountByParent.get(p.id) || 0;
      p.replyCount = totalChildren - reactionCount; // 리액션 제외 실제 답글 수
    });

    // 🔧 reactions를 allMessages에 추가하여 UIMessage.reactions()에서 사용 가능하도록
    if (reactions.length > 0) {
      this.allMessages = this.dedupeMessages(this.allMessages.concat(reactions));
    }
  }

  ingestLoadedChildren(parentId: string, children: Message[]) {
    // 1. Merge and dedupe first to get the final message list
    this.allMessages = this.dedupeMessages(this.allMessages.concat(children));
    this.oldMessages = this.dedupeMessages(this.oldMessages.concat(children));

    // 2. Now find the parent in the FINAL list and set the flags
    const parent = this.allMessages.find(m => m.id === parentId);
    if (parent) {
      parent.repliesLoaded = true;
      // 🔧 리액션(emoticon)은 댓글 수에서 제외
      parent.replyCount = children.filter(c => !c.emoticon).length;
    }

    // Refresh UI - Pass empty arrays for visible/invisible updates to prevent "New Message" toasts
    let _messages = this.getUIMessages();
    this.onNewMessages(_messages, false, [], []);
  }

  async ingestMessages(messages: Message[]) {
    await this.loadSummaries(messages);
    this.allMessages = this.dedupeMessages(this.allMessages.concat(messages));
    this.oldMessages = this.dedupeMessages(this.oldMessages.concat(messages));

    let _uiMessages = this.getUIMessages();
    // Do not pass messages to visible list and set newParentMessages to FALSE 
    // to avoid triggering bottom scroll during background ingest
    this.onNewMessages(_uiMessages, false, [], []);
  }
}
